import {
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  IconButton,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditIcon from '@mui/icons-material/Edit'
import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  useDecideApproval,
  useHalls,
  useOutlets,
  usePendingApprovals,
  useRemoveHall,
  useRemoveOutlet,
} from '@/api/hooks'
import type { Hall, Outlet } from '@/api/types'
import { useAuthStore } from '@/auth/authStore'
import { EceebillImportTab } from '@/pages/settings/EceebillImportTab'
import { NumberingTab } from '@/pages/settings/NumberingTab'
import { OutletDialog } from '@/pages/settings/OutletDialog'
import { PoliciesTab } from '@/pages/settings/PoliciesTab'
import { TermsTab } from '@/pages/settings/TermsTab'
import { CompanyTab } from '@/pages/settings/CompanyTab'
import { ModulesTab } from '@/pages/settings/ModulesTab'
import { DiscountsTab } from '@/pages/settings/DiscountsTab'
import { BillingTab } from '@/pages/settings/BillingTab'
import { InvoicePrintTab } from '@/pages/settings/InvoicePrintTab'
import { KitchensTab } from '@/pages/settings/KitchensTab'
import { DepartmentsTab } from '@/pages/settings/DepartmentsTab'
import { OccasionsTab } from '@/pages/settings/OccasionsTab'
import { SessionsTab } from '@/pages/settings/SessionsTab'
import { OrderLimitsTab } from '@/pages/settings/OrderLimitsTab'
import { ServicesTab } from '@/pages/settings/ServicesTab'
import { SpaceDialog } from '@/pages/settings/SpaceDialog'
import {
  EmptyState,
  ErrorBanner,
  formatDateTime,
  formatMoney,
  formatNumber,
  formatSlotHours,
  humanise,
  PageHeader,
  StatusChip,
} from '@/components/common'

const TAB_KEYS = [
  'company',
  'modules',
  'outlets',
  'services',
  'occasions',
  'sessions',
  'order-limits',
  'halls',
  'dining',
  'kitchens',
  'numbering',
  'policies',
  'discounts',
  'billing',
  'invoice-print',
  'terms',
  'approvals',
  'departments',
  'eceebill-import',
] as const

export function SettingsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const can = useAuthStore((state) => state.can)
  const requested = searchParams.get('tab')
  const initial = Math.max(TAB_KEYS.indexOf((requested ?? 'company') as (typeof TAB_KEYS)[number]), 0)
  const [tab, setTab] = useState(initial)

  return (
    <>
      <PageHeader
        title="Settings"
        subtitle="Organisation setup, approvals waiting on you, and your notification inbox"
      />
      <Card>
        <Tabs
          value={tab}
          onChange={(_event, value) => {
            setTab(value)
            setSearchParams({ tab: TAB_KEYS[value] })
          }}
          variant="scrollable"
        >
          <Tab label="Company" />
          <Tab label="Modules" />
          <Tab label="Outlets" />
          <Tab label="Services" />
          <Tab label="Occasions" />
          <Tab label="Sessions" />
          <Tab label="Order limits" />
          <Tab label="Halls" />
          <Tab label="Dining" />
          <Tab label="Kitchens" />
          <Tab label="Numbering" />
          <Tab label="Policies" />
          <Tab label="Discounts" />
          <Tab label="Billing" />
          <Tab label="Invoice print" />
          <Tab label="Terms" />
          <Tab label="Approvals" />
          <Tab label="Departments" />
          {/* Last, and only for a platform administrator: it enters orders with
              the numbers they carried, which nobody else may do. */}
          {can('platform.admin') ? <Tab label="ECEEBILL import" /> : null}
        </Tabs>
        <Divider />
        <CardContent>
          {tab === 0 && <CompanyTab />}
          {tab === 1 && <ModulesTab />}
          {tab === 2 && <OutletsTab />}
          {tab === 3 && <ServicesTab />}
          {tab === 4 && <OccasionsTab />}
          {tab === 5 && <SessionsTab />}
          {tab === 6 && <OrderLimitsTab />}
          {tab === 7 && <SpacesTab spaceType="HALL" />}
          {tab === 8 && <SpacesTab spaceType="DINING" />}
          {tab === 9 && <KitchensTab />}
          {tab === 10 && <NumberingTab />}
          {tab === 11 && <PoliciesTab />}
          {tab === 12 && <DiscountsTab />}
          {tab === 13 && <BillingTab />}
          {tab === 14 && <InvoicePrintTab />}
          {tab === 15 && <TermsTab />}
          {tab === 16 && <ApprovalsTab />}
          {tab === 17 && <DepartmentsTab />}
          {tab === 18 && can('platform.admin') && <EceebillImportTab />}
        </CardContent>
      </Card>
    </>
  )
}

function OutletsTab() {
  const canWrite = useAuthStore((state) => state.canWrite)
  const { data, error } = useOutlets()
  const [editing, setEditing] = useState<Outlet | null>(null)
  const [creating, setCreating] = useState(false)
  const remove = useRemoveOutlet()

  return (
    <>
      <ErrorBanner error={error ?? remove.error} />
      {canWrite('org') ? (
        <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
          <Button size="small" startIcon={<AddIcon />} onClick={() => setCreating(true)}>
            New outlet
          </Button>
        </Stack>
      ) : null}
      {!data || data.length === 0 ? (
        <EmptyState message="No outlets are visible to you." />
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Code</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>City</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Capabilities</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((outlet) => (
              <TableRow key={outlet.id} hover>
                <TableCell>{outlet.code}</TableCell>
                <TableCell>
                  {outlet.name}
                  {/* Whose it is, for anybody who administers more than one
                      business and cannot tell two "Central Kitchen"s apart. */}
                  {outlet.companyName ? (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {outlet.companyName}
                      {(outlet.companyIds?.length ?? 1) > 1
                        ? ` · shared with ${(outlet.companyIds?.length ?? 1) - 1} other`
                        : ''}
                    </Typography>
                  ) : null}
                </TableCell>
                <TableCell>{humanise(outlet.outletType)}</TableCell>
                <TableCell>{outlet.city ?? '—'}</TableCell>
                <TableCell>{outlet.phone ?? '—'}</TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5}>
                    {outlet.billingLocation && <Chip size="small" variant="outlined" label="Billing" />}
                    {outlet.productionKitchen && <Chip size="small" variant="outlined" label="Kitchen" />}
                  </Stack>
                </TableCell>
                <TableCell align="right">
                  {canWrite('org') ? (
                    <>
                      <IconButton size="small" onClick={() => setEditing(outlet)} aria-label={`Edit ${outlet.name}`}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        aria-label={`Withdraw ${outlet.name}`}
                        disabled={remove.isPending}
                        onClick={() => remove.mutate(outlet.id)}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </>
                  ) : null}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <OutletDialog open={creating} onClose={() => setCreating(false)} />
      <OutletDialog open={Boolean(editing)} outlet={editing} onClose={() => setEditing(null)} />
    </>
  )
}

/**
 * Banquet halls and dining areas, which are the same thing to everything that
 * books them and differ only in what they are called.
 */
function SpacesTab({ spaceType }: { spaceType: 'HALL' | 'DINING' }) {
  const canWrite = useAuthStore((state) => state.canWrite)
  const { data, error } = useHalls(undefined, spaceType)
  const { data: outlets } = useOutlets()
  const [editing, setEditing] = useState<Hall | null>(null)
  const [creating, setCreating] = useState(false)
  const remove = useRemoveHall()

  const dining = spaceType === 'DINING'
  const noun = dining ? 'dining area' : 'hall'
  const outletName = new Map((outlets ?? []).map((outlet) => [outlet.id, outlet.name]))

  return (
    <>
      <ErrorBanner error={error ?? remove.error} />
      {canWrite('org') ? (
        <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
          <Button size="small" startIcon={<AddIcon />} onClick={() => setCreating(true)}>
            New {noun}
          </Button>
        </Stack>
      ) : null}
      {!data || data.length === 0 ? (
        <EmptyState
          message={dining ? 'No dining areas configured.' : 'No banquet halls configured.'}
          hint={canWrite('org') ? `Add one and map it to the outlet it belongs to.` : undefined}
        />
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{dining ? 'Dining area' : 'Hall'}</TableCell>
              <TableCell>Outlet</TableCell>
              <TableCell align="right">Seating</TableCell>
              <TableCell align="right">Floating</TableCell>
              <TableCell align="right">Minimum guarantee</TableCell>
              <TableCell align="right">Rental</TableCell>
              <TableCell align="right">Buffers</TableCell>
              <TableCell>Slots</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((hall) => (
              <TableRow key={hall.id} hover>
                <TableCell>
                  <Typography variant="body2">{hall.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {hall.floorLocation ?? hall.code}
                  </Typography>
                </TableCell>
                <TableCell>{outletName.get(hall.outletId) ?? '—'}</TableCell>
                <TableCell align="right">{formatNumber(hall.seatingCapacity)}</TableCell>
                <TableCell align="right">{formatNumber(hall.floatingCapacity)}</TableCell>
                <TableCell align="right">{formatNumber(hall.minimumGuarantee)}</TableCell>
                <TableCell align="right">{formatMoney(hall.rentalCharge)}</TableCell>
                <TableCell align="right">
                  <Typography variant="caption">
                    {hall.setupBufferMinutes}m setup · {hall.cleanupBufferMinutes}m cleanup
                  </Typography>
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    {hall.timeSlots.map((slot) => (
                      <Chip
                        key={slot.id}
                        size="small"
                        variant="outlined"
                        label={`${slot.name} ${formatSlotHours(slot.startTime, slot.endTime)}`}
                        title={formatMoney(slot.rentalCharge)}
                      />
                    ))}
                  </Stack>
                </TableCell>
                <TableCell align="right">
                  {canWrite('org') ? (
                    <>
                      <IconButton size="small" onClick={() => setEditing(hall)} aria-label={`Edit ${hall.name}`}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        aria-label={`Withdraw ${hall.name}`}
                        disabled={remove.isPending}
                        onClick={() => remove.mutate(hall.id)}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </>
                  ) : null}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <SpaceDialog open={creating} spaceType={spaceType} onClose={() => setCreating(false)} />
      <SpaceDialog
        open={Boolean(editing)}
        space={editing}
        spaceType={spaceType}
        onClose={() => setEditing(null)}
      />
    </>
  )
}

function ApprovalsTab() {
  const { data, error } = usePendingApprovals()
  const decide = useDecideApproval()
  const user = useAuthStore((state) => state.user)

  return (
    <>
      <ErrorBanner error={error ?? decide.error} />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Discounts, refunds, extra manpower and order changes route here when they exceed the
        requester's authority. Signed in as {user?.roles.join(', ')}.
      </Typography>
      {!data || data.length === 0 ? (
        <EmptyState message="Nothing is waiting on your sign-off." />
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Type</TableCell>
              <TableCell>Reference</TableCell>
              <TableCell align="right">Value</TableCell>
              <TableCell>Required role</TableCell>
              <TableCell>Reason</TableCell>
              <TableCell>Raised</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((approval) => (
              <TableRow key={approval.id} hover>
                <TableCell>
                  <Typography variant="body2">{humanise(approval.approvalType)}</Typography>
                  {/* Discounts carry a document number; the rest go by id. */}
                  {approval.approvalNumber && (
                    <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                      {approval.approvalNumber}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="caption">{humanise(approval.referenceType)}</Typography>
                </TableCell>
                <TableCell align="right">
                  {approval.requestedValue === undefined || approval.requestedValue === null
                    ? '—'
                    : formatNumber(approval.requestedValue)}
                </TableCell>
                <TableCell>
                  <StatusChip status={approval.requiredRoleCode} />
                </TableCell>
                <TableCell sx={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {approval.reason ?? '—'}
                </TableCell>
                <TableCell>{formatDateTime(approval.createdAt)}</TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button
                      size="small"
                      color="error"
                      onClick={() => decide.mutate({ id: approval.id, approved: false })}
                    >
                      Reject
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => decide.mutate({ id: approval.id, approved: true })}
                    >
                      Approve
                    </Button>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </>
  )
}
